import { useEffect } from 'react';

// 버전 정보 타입 정의
interface VersionInfo {
    version: string;
    buildTime: string;
}

// 서비스워커 메시지 타입 정의
interface ServiceWorkerMessage {
    type: string;
    version: string;
}

// 버전 관리 컴포넌트 props 타입
interface VersionManagerProps {
    children: React.ReactNode;
}

/**
 * 애플리케이션의 버전을 관리하고 자동 새로고침 기능을 제공하는 컴포넌트
 * 
 * 두 가지 주요 기능 제공:
 * 1. 서비스 워커로부터 새 버전 알림을 받으면 자동 새로고침
 * 2. 모듈 로드 오류 감지 시 자동 새로고침 (404 오류 등)
 */
export const VersionManager = ({ children }: VersionManagerProps) => {
    useEffect(() => {
        // 현재 버전 상태 초기화
        if (!localStorage.getItem('app_version')) {
            fetch('/version.json')
                .then(res => res.json())
                .then((data: VersionInfo) => {
                    localStorage.setItem('app_version', data.version + '-' + data.buildTime);
                })
                .catch(console.error);
        }

        // 모듈 로드 실패 감지 및 자동 새로고침
        const handleError = (event: ErrorEvent) => {
            if (
                event.message && 
                (event.message.includes('Failed to fetch dynamically imported module') ||
                event.message.includes('404'))
            ) {
                console.log('모듈 로드 실패로 인한 자동 새로고침');
                const refreshCount = sessionStorage.getItem('refresh_count') || '0';
                if (parseInt(refreshCount) < 2) {
                    sessionStorage.setItem('refresh_count', (parseInt(refreshCount) + 1).toString());
                    window.location.reload();
                } else {
                    console.error('여러 번의 새로고침 후에도 문제가 해결되지 않았습니다.');
                }
            }
        };

        // 서비스 워커 메시지 핸들러
        const handleServiceWorkerMessage = (event: MessageEvent) => {
            const data = event.data as ServiceWorkerMessage;
            if (data && data.type === 'CHECK_VERSION') {
                const currentVersion = localStorage.getItem('app_version');
                const newVersion = data.version;
                
                // 버전이 다르면 자동 새로고침
                if (currentVersion && currentVersion !== newVersion) {
                    console.log('새 버전 감지:', newVersion);
                    localStorage.setItem('app_version', newVersion);
                    // 자동 새로고침
                    window.location.reload();
                }
            }
        };

        // 이벤트 리스너 등록
        window.addEventListener('error', handleError);
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
        }

        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        return () => {
            window.removeEventListener('error', handleError);
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
            }
        };
    }, []);

    return <>{children}</>;
};

export default VersionManager;
